import { useEffect, useState } from 'react';
import { DynamicContentListData } from './model';
import { Tab } from '../../../../components/blackrock/advisor-center/_types/tab';
import Filter from './parts/filter/Filter';
import Header from './parts/header/Header';
import Toolbar from './parts/toolbar/Toolbar';
import LoadAndEmptyStatesWrapper from '../../../../components/blackrock/advisor-center/empty-state/LoadAndEmptyStatesWrapper';
import { useSaveForSeriesItemList } from '../../../../components/blackrock/advisor-center/save/hooks/useSaveForSeriesItemList';
import { useSort } from './hooks/useSort.tsx';
import { useFilter } from './hooks/useFilter.tsx';
import List from './parts/list/List.tsx';
import { useTab } from './hooks/useTab.tsx';
import { useLoad } from './hooks/useLoad.tsx';
import Tabs, {
	getTabPanelAttributes,
} from '../../../../components/blackrock/advisor-center/tabs/Tabs.tsx';
import CallToAction from './parts/call-to-action/CallToAction.tsx';
import {
	DEFAULT_SORT,
	DEFAULT_SORT_ORDER,
	ITEM_PER_PAGE,
} from './dynamic-content.config.ts';
import { useScrollRestoration } from '../../../../_helpers/hooks/useScrollRestoration.tsx';
import Style from '../../../../components/blackrock/advisor-center/style/Style.tsx';
import { SeriesItemData } from '../../../../components/blackrock/advisor-center/cards/series-item-card/SeriesItemCardProps.ts';
import { useComponentContext } from '../../../../components/blackrock/advisor-center/_helpers/componentContext.tsx';
import { usePendingSaveDataLinkEventHandler } from '../../../../_helpers/hooks/usePendingDataLinkEventHandler.ts';
import { FilterConfig } from '../../../../components/blackrock/advisor-center/filter-dialog/model.ts';

import './DynamicContentList.scss';
import './DynamicContentListCard.scss';
import Select from '../../../../components/blackrock/advisor-center/select/Select.tsx';

export type DynamicContentListProps = {
	ajaxUrl?: string;
	initialFilter?: FilterConfig;
	data: DynamicContentListData;
};

const DynamicContentList: React.FC<DynamicContentListProps> = ({
	ajaxUrl,
	initialFilter,
	data,
}) => {
	useScrollRestoration('manual');
	const componentId = useComponentContext().componentId;
	usePendingSaveDataLinkEventHandler(data.saveConfig?.pendingDataLinkEvent);
	const [items, setItems] = useState<SeriesItemData[]>(data.contentList || []);
	const [loadingState, retry] = useLoad(ajaxUrl, items, setItems);
	const sortDisabled = data.sortDisabled || data.variant === 'dashboard';
	const [sortedList, sortOptions, setSelectedSort] = useSort(
		items,
		data.translationMap.sort,
		sortDisabled
	);
	const filterDisabled = data.filterDisabled || data.variant === 'dashboard';
	const [filteredList, filterConfig, filter, setFilter, getFilteredList] =
		useFilter(
			sortedList,
			data,
			data.translationMap,
			initialFilter,
			filterDisabled
		);
	const [tabbedList, selectedTab, setSelectedTab] = useTab(
		filteredList,
		data.secondTabTopic
	);
	const [currentPage, setCurrentPage] = useState<number>(1);

	const changeSavedState = useSaveForSeriesItemList(setItems, data.saveConfig);

	const isCTAButtonVisible: boolean =
		(data.variant === 'dashboard' && !!tabbedList.length) ||
		tabbedList.length > ITEM_PER_PAGE * currentPage;

	// reset current page to default 1
	useEffect(() => {
		setCurrentPage(1);
	}, [selectedTab, filter]);

	const onClickLoadMoreButton = (): void => {
		const newPage = currentPage + 1;
		setCurrentPage(newPage);
	};

	return (
		<Style>
			<div className="blk-ac-dynamic-content-list">
				<div className={`${data.variant}-layout`}>
					<Header
						title={data.componentTitle}
						description={data.componentDescription}
						titleLvl={3}
					/>
					<Toolbar>
						<Tabs
							tabList={data.tabs as Tab[]}
							selectedTabIndex={selectedTab}
							tabChange={setSelectedTab}
						/>

						{!(sortDisabled && filterDisabled) && !!items?.length && (
							<div className="sort-and-filter">
								{!sortDisabled && (
									<div className="sort-container">
										<Select
											orderLabels={{
												ascending: data.translationMap.sort.ascending,
												descending: data.translationMap.sort.descending,
											}}
											label={data.translationMap.sort.byLabel}
											options={sortOptions}
											defaultOption={DEFAULT_SORT}
											defaultOrder={DEFAULT_SORT_ORDER}
											onSelect={setSelectedSort}
										></Select>
									</div>
								)}
								{!filterDisabled && (
									<Filter
										filter={filter}
										filterConfig={filterConfig}
										translation={data.translationMap}
										calcFilterResult={(newFilter) =>
											getFilteredList(newFilter).length
										}
										setFilter={setFilter}
									/>
								)}
							</div>
						)}
					</Toolbar>
					<LoadAndEmptyStatesWrapper
						loadingState={loadingState}
						networkErrorConfig={data.networkErrorConfig}
						retryCallback={retry}
						isNoResult={tabbedList?.length === 0}
						// eslint-disable-next-line @typescript-eslint/ban-ts-comment
						// @ts-ignore
						noResultConfig={data.noResultConfig}
					>
						<div
							className="content-list"
							{...getTabPanelAttributes(
								componentId,
								selectedTab,
								data.tabs.length > 1
							)}
						>
							<List
								list={tabbedList}
								data={data}
								translation={data.translationMap}
								onChangeSavedState={changeSavedState}
								currentPage={currentPage}
							/>
						</div>
					</LoadAndEmptyStatesWrapper>

					<CallToAction
						data={data}
						isVisible={isCTAButtonVisible}
						onClick={onClickLoadMoreButton}
					/>
				</div>
			</div>
		</Style>
	);
};

export default DynamicContentList;
